import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingDown, Play, Target } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SUBJECT_LABELS } from "@/lib/pyqMeta";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export default function ChapterPractice() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [subject, setSubject] = useState("");
  const [chapters, setChapters] = useState([]);
  const [weakest, setWeakest] = useState([]);
  const [loading, setLoading] = useState(true);

  const subjects = useMemo(() => {
    if (!meta) return [];
    const all = new Set();
    Object.values(meta.examSubjects || {}).forEach((arr) => arr.forEach((s) => all.add(s)));
    return [...all];
  }, [meta]);

  useEffect(() => {
    api.get("/pyq/meta").then((r) => setMeta(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/stats/chapters", { params: subject ? { subject } : {} })
      .then((r) => { setChapters(r.data.chapters); setWeakest(r.data.weakest); })
      .finally(() => setLoading(false));
  }, [subject]);

  const drill = (subj, chapter) => {
    const p = new URLSearchParams({ subject: subj, chapter, mode: "UNTIMED" });
    navigate(`/student/session?${p.toString()}`);
  };

  const attemptedChapters = chapters.filter((c) => c.attempted > 0);

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">Chapter Practice</h1>
          <p className="mt-2 text-muted-foreground">Drill a single chapter and see which topics are dragging your score down.</p>
        </div>
        <div className="w-52">
          <Select value={subject || "all"} onValueChange={(v) => setSubject(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="chapter-subject-filter" className="rounded-xl"><SelectValue placeholder="All subjects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All subjects</SelectItem>
              {subjects.map((s) => <SelectItem key={s} value={s}>{SUBJECT_LABELS[s] || s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Weak areas */}
      {weakest.length > 0 && (
        <div className="mb-8 rounded-2xl border border-red-500/25 bg-red-50/50 p-6 dark:bg-red-950/20" data-testid="weak-areas">
          <div className="mb-4 flex items-center gap-2 text-red-600 dark:text-red-400">
            <TrendingDown className="h-5 w-5" />
            <span className="font-heading text-lg font-semibold">Your weakest chapters</span>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {weakest.map((c) => (
              <button key={`${c.subject}-${c.chapter}`} onClick={() => drill(c.subject, c.chapter)} data-testid={`weak-${c.chapter}`} className="rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/40">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{SUBJECT_LABELS[c.subject] || c.subject}</div>
                <div className="mt-1 font-medium leading-tight">{c.chapter}</div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="font-heading text-2xl font-bold text-red-500">{c.accuracy}%</span>
                  <span className="text-xs text-muted-foreground">{c.correct}/{c.attempted}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* All chapters with your accuracy */}
      <h2 className="mb-4 font-heading text-lg font-semibold">
        {attemptedChapters.length > 0 ? "Your chapter performance" : "Pick a chapter to start"}
      </h2>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}</div>
      ) : attemptedChapters.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center" data-testid="chapter-empty">
          <Target className="mx-auto mb-3 h-8 w-8 text-primary" />
          <p className="font-heading text-lg font-semibold">No chapter data yet</p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">Attempt some questions and your per-chapter accuracy will appear here. Meanwhile, browse chapters in the practice hub.</p>
          <Button onClick={() => navigate("/student/practice")} className="mt-5 rounded-full px-6">Go to practice hub</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {attemptedChapters.map((c, i) => (
            <motion.div
              key={`${c.subject}-${c.chapter}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: Math.min(i * 0.03, 0.3) }}
              className="flex items-center gap-4 rounded-xl border border-border bg-card p-4"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium">{c.chapter}</span>
                  <span className="text-xs text-muted-foreground">· {SUBJECT_LABELS[c.subject] || c.subject}</span>
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <Progress value={c.accuracy} className="h-1.5 flex-1" />
                  <span className={cn("w-12 text-right text-sm font-semibold", c.accuracy < 50 ? "text-red-500" : c.accuracy < 75 ? "text-amber-500" : "text-green-600 dark:text-green-400")}>{c.accuracy}%</span>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => drill(c.subject, c.chapter)} data-testid={`drill-${c.chapter}`} className="rounded-full">
                <Play className="mr-1.5 h-3.5 w-3.5" /> Drill
              </Button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
